import React, { useCallback, useRef, useState } from "react"
import { ArrowLeft, ArrowRight, Info, Pencil, Search, Trash2, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import TransportTypeIcon from "../TransportTypeIcon"
import PriceCell from "../PriceCell"
import FloatingPanel from "../FloatingPanel"

const InfoCell = ({ text }) => {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])

  if (!text) return <span className="text-app-content-faint">—</span>

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mx-auto flex h-8 w-8 items-center justify-center rounded-full text-brand-main hover:bg-brand-main/10 transition-colors"
      >
        <Info className="w-5 h-5" />
      </button>
      {open && (
        <FloatingPanel anchorEl={ref.current} onClose={close} width={320}>
          <p className="max-h-[280px] overflow-y-auto whitespace-pre-line p-4 text-sm text-app-content">
            {text}
          </p>
        </FloatingPanel>
      )}
    </>
  )
}

const DirectionBadge = ({ direction }) => {
  const { t } = useTranslation()
  const isReturn = direction === "return"
  const Icon = isReturn ? ArrowLeft : ArrowRight

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] sm:text-xs font-medium ${
        isReturn
          ? "bg-amber-50 text-amber-600 border-amber-100"
          : "bg-emerald-50 text-emerald-600 border-emerald-100"
      }`}
    >
      <Icon className="w-3 h-3" />
      {isReturn
        ? t("admin.direction_return", { defaultValue: "Обратно" })
        : t("admin.direction_departure", { defaultValue: "Туда" })}
    </span>
  )
}

const EditRouteButton = ({ route, buildRouteEditUrl }) => {
  const { t } = useTranslation()
  if (!route.base_price_id) return null
  return (
    <a
      href={buildRouteEditUrl(route.base_price_id)}
      title={t("admin.edit_route_common", { defaultValue: "Изменить общие данные трассы" })}
      className="p-1.5 text-text-secondary hover:text-brand-main"
    >
      <Pencil className="w-4 h-4" />
    </a>
  )
}

const DeleteRouteButton = ({ route, busy, onDelete }) => {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onDelete(route.price_ids, route.name)}
      title={t("admin.delete_route", { defaultValue: "Удалить трассу со всеми ценами" })}
      className="p-1.5 text-red-500 hover:text-red-600 disabled:opacity-disabled"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
    </button>
  )
}

const PricesTable = ({
  routes,
  transportTypes,
  buildNewUrl,
  buildEditUrl,
  buildRouteEditUrl,
  onDelete,
  busyIds,
  transferTypeLabels,
}) => {
  const { t } = useTranslation()

  if (!routes.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-surface-main rounded-card border border-app-content-faint/10">
        <Search className="w-10 h-10 mb-4 text-text-muted" />
        <p className="text-text-secondary text-lg font-medium">
          {t("admin.nothing_found", { defaultValue: "Ничего не найдено" })}
        </p>
        <p className="text-text-muted text-sm mt-1">
          {t("admin.search_adjust_hint", { defaultValue: "Попробуйте изменить фильтры" })}
        </p>
      </div>
    )
  }

  const th = "px-3 py-3 text-center align-bottom"
  const isRowBusy = (r) => r.price_ids.some((id) => busyIds.includes(id))

  const renderCell = (r, tt) => (
    <PriceCell
      prices={r.prices_by_type[tt.id]}
      typeId={tt.id}
      baseId={r.base_price_id}
      buildNewUrl={buildNewUrl}
      buildEditUrl={buildEditUrl}
      onDelete={onDelete}
      busyIds={busyIds}
      transferTypeLabels={transferTypeLabels}
    />
  )

  return (
    <>
      {/* ── Десктоп / планшет: таблица на всю ширину, горизонтальный скролл при нехватке места ── */}
      <div className="hidden md:block w-full overflow-x-auto rounded-card border border-app-content-faint/10 bg-surface-main shadow-sm">
        <table className="w-full min-w-max border-collapse">
          <thead>
            <tr className="bg-bg-secondary">
              <th className="sticky left-0 z-10 min-w-[260px] max-w-[460px] bg-bg-secondary px-4 py-3 text-left text-sm font-semibold text-app-base-strong">
                {t("admin.route_direction", { defaultValue: "Трасса / направление" })}
              </th>
              <th className={`${th} min-w-[110px] text-sm font-semibold text-app-base-strong`}>
                {t("admin.important_info", { defaultValue: "Важная информация" })}
              </th>
              {transportTypes.map((tt) => (
                <th key={tt.id} className={`${th} min-w-[120px] max-w-[180px]`}>
                  <div className="flex flex-col items-center gap-1.5">
                    <TransportTypeIcon icon={tt.icon_url} name={tt.name} />
                    <span className="text-xs font-semibold leading-tight text-app-base-strong">
                      {tt.name}
                    </span>
                  </div>
                </th>
              ))}
              <th className={`${th} w-[90px]`} />
            </tr>
          </thead>

          <tbody>
            {routes.map((r) => (
              <tr
                key={r.key}
                className="group border-t border-app-content-faint/10 hover:bg-bg-secondary/40 transition-colors"
              >
                <td className="sticky left-0 z-10 min-w-[260px] max-w-[460px] bg-surface-main px-4 py-3 group-hover:bg-bg-secondary">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="font-semibold text-app-base-strong leading-tight break-words">
                      {r.name || "—"}
                    </p>
                    <DirectionBadge direction={r.direction} />
                  </div>
                  {r.company_name && (
                    <p className="mt-0.5 text-xs text-text-muted">{r.company_name}</p>
                  )}
                </td>

                <td className="px-3 py-3 text-center">
                  <InfoCell text={r.important_info} />
                </td>

                {transportTypes.map((tt) => (
                  <td key={tt.id} className="px-2 py-3 text-center">
                    {renderCell(r, tt)}
                  </td>
                ))}

                <td className="px-3 py-3 text-center">
                  <div className="flex items-center justify-center">
                    <EditRouteButton route={r} buildRouteEditUrl={buildRouteEditUrl} />
                    <DeleteRouteButton route={r} busy={isRowBusy(r)} onDelete={onDelete} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Мобильный вид: карточка на трассу, типы транспорта — плитки ── */}
      <div className="md:hidden flex flex-col gap-3">
        {routes.map((r) => (
          <div
            key={r.key}
            className="rounded-card border border-app-content-faint/10 bg-surface-main p-4 shadow-sm"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="font-semibold text-app-base-strong leading-snug break-words">
                    {r.name || "—"}
                  </p>
                  <DirectionBadge direction={r.direction} />
                </div>
                {r.company_name && (
                  <p className="mt-0.5 text-xs text-text-muted">{r.company_name}</p>
                )}
              </div>
              {r.important_info && <InfoCell text={r.important_info} />}
              <EditRouteButton route={r} buildRouteEditUrl={buildRouteEditUrl} />
              <DeleteRouteButton route={r} busy={isRowBusy(r)} onDelete={onDelete} />
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {transportTypes.map((tt) => (
                <div
                  key={tt.id}
                  className="flex min-w-0 flex-col items-center gap-1.5 rounded-input border border-app-content-faint/10 bg-bg-secondary/40 px-2 py-2.5 text-center"
                >
                  <TransportTypeIcon icon={tt.icon_url} name={tt.name} className="w-7 h-7" />
                  <span className="text-[11px] leading-tight text-text-secondary break-words">
                    {tt.name}
                  </span>
                  {renderCell(r, tt)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export default PricesTable
